import { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import type { Activity, Audience, EmailMessage, Herkunft, Wegstation } from '@epasu/core';
import {
  CUSTOMER_STATUS_LABELS,
  CUSTOMER_STATUSES,
  ORGANIZATION_STATUS_LABELS,
  ZIELGRUPPEN_OHNE_PROJEKT,
  type CustomerStatus,
} from '@epasu/core/constants';
import {
  ArrowLeft,
  Briefcase,
  Building2,
  Globe,
  Handshake,
  Link2,
  Mail,
  Map,
  MapPin,
  Pencil,
  Phone,
  StickyNote,
  Trash2,
  UserX,
} from 'lucide-react';
import {
  crm,
  type ContactRow,
  type CustomerRow,
  type OrderRow,
  type OrganizationRow,
  type ProjectRow,
} from '@/lib/crm-client';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Select } from '@/components/ui/input';
import { ComposeDialog } from '@/components/ComposeDialog';
import { OrganizationDialog } from '@/components/OrganizationDialog';
import { DemoteCustomerDialog } from '@/components/DemoteCustomerDialog';
import { PromoteCustomerDialog } from '@/components/PromoteCustomerDialog';
import { DeleteOrganizationDialog, STATUS_VARIANT } from '@/pages/OrganizationsPage';
import { listLabel } from '@/lib/backLink';
import { cn } from '@/lib/utils';
import { CampaignsTab } from './CampaignsTab';
import { ContactsTab } from './ContactsTab';
import { MailTab } from './MailTab';
import { OrdersTab } from './OrdersTab';
import { TimelineTab } from './TimelineTab';
import { ProjectsTab } from './ProjectsTab';
import { UeberblickTab } from './UeberblickTab';
import { Wegleiste } from './Wegleiste';

type Reiter =
  | 'overview'
  | 'projects'
  | 'contacts'
  | 'campaigns'
  | 'mail'
  | 'orders'
  | 'timeline';

const REITER: { key: Reiter; label: string; nurKunde?: boolean }[] = [
  { key: 'overview', label: 'Überblick' },
  { key: 'projects', label: 'Projekte', nurKunde: true },
  { key: 'contacts', label: 'Ansprechpartner' },
  { key: 'campaigns', label: 'Kampagnen' },
  { key: 'mail', label: 'Schriftverkehr' },
  { key: 'orders', label: 'Aufträge' },
  { key: 'timeline', label: 'Verlauf' },
];

/**
 * Die Firmenakte.
 *
 * Eine Akte je Firma, vom ersten Kaltmail bis zum laufenden Projekt: der Kopf
 * trägt Zielgruppe und Stand als Chips und darunter den Weg, den die Firma
 * gegangen ist (`Wegleiste`). „Überblick“ ist der erste Reiter und führt mit
 * vier Kacheln zu den übrigen: Projekt, Herkunft, Schriftverkehr,
 * Ansprechpartner. Dazu Ansprechpartner, Kampagnen, Aufträge und der gemeinsame
 * Verlauf — je ein Reiter, dessen Daten hier geladen werden.
 *
 * „Projekte“ gibt es nur, wo Projekte möglich sind: nicht bei Zielgruppen
 * ohne Projektarbeit (Fotografen) und nicht vor der Beförderung zum Kunden. Der
 * Reiter lädt seine Projekte selbst, weil sie am Kunden hängen, nicht an der
 * Firma; hier werden sie für den Überblick einmal mitgezogen.
 */
export function OrganizationDetailPage() {
  const { organizationId } = useParams<{ organizationId: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const [org, setOrg] = useState<OrganizationRow | null>(null);
  const [kunde, setKunde] = useState<CustomerRow | null>(null);
  const [kontakte, setKontakte] = useState<ContactRow[]>([]);
  const [mails, setMails] = useState<EmailMessage[]>([]);
  const [auftraege, setAuftraege] = useState<OrderRow[]>([]);
  const [verlauf, setVerlauf] = useState<Activity[]>([]);
  const [weg, setWeg] = useState<Wegstation[]>([]);
  const [herkunft, setHerkunft] = useState<Herkunft | null>(null);
  const [projekte, setProjekte] = useState<ProjectRow[]>([]);
  const [audiences, setAudiences] = useState<Audience[]>([]);

  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [hinweis, setHinweis] = useState<string | null>(null);

  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [befoerdern, setBefoerdern] = useState(false);
  const [zurueckstufen, setZurueckstufen] = useState(false);
  const [composing, setComposing] = useState(false);

  // Der Überblick ist für jede Firma der Anfang. Ein Reload fasst den Reiter
  // nicht an, sonst würde jede Aktualisierung den Nutzer von seinem gewählten
  // Reiter zurückwerfen.
  const [tab, setTab] = useState<Reiter>('overview');

  const load = useCallback(async () => {
    if (!organizationId) return;
    const gefunden = await crm.organizations.get(organizationId);
    if (!gefunden) {
      setNotFound(true);
      setLoading(false);
      return;
    }
    // Die Zielgruppen gehören in denselben Zug: solange sie fehlen, kennt die
    // Akte den Schlüssel nicht und hielte Projekte auch bei Fotografen für möglich.
    const [k, m, a, v, kd, w, h, au] = await Promise.all([
      crm.contacts.list({ organizationId }),
      crm.organizations.mail(organizationId),
      crm.orders.list({ organizationId }),
      crm.organizations.timeline(organizationId),
      crm.customers.byOrganization(organizationId),
      crm.organizations.weg(organizationId),
      crm.organizations.herkunft(organizationId),
      crm.audiences.list(),
    ]);
    // Projekte hängen am Kunden, nicht an der Firma — erst fragen, wenn er da ist.
    const p = kd ? await crm.projects.list({ customerId: kd.id, includeClosed: true }) : [];
    setOrg(gefunden);
    setKontakte(k);
    setMails(m);
    setAuftraege(a);
    setVerlauf(v);
    setKunde(kd);
    setWeg(w);
    setHerkunft(h);
    setAudiences(au);
    setProjekte(p);
    setLoading(false);
  }, [organizationId]);

  useEffect(() => {
    void load();
  }, [load]);

  const statusSetzen = useCallback(
    async (status: CustomerStatus) => {
      if (!kunde) return;
      await crm.customers.update(kunde.id, { status });
      await load();
    },
    [kunde, load],
  );

  // Loading-Rueckgabe kommt vor dieser Stelle: solange `loading` steht, ist
  // weder `org` noch `kunde` verlaesslich, und mit ihnen faellig auch das
  // Rueckziel — das verhindert, dass das Label kurz „Alle Firmen“ zeigt und
  // dann auf „Alle Kunden“ umspringt, sobald der Kunde eintrifft.
  if (loading) return <p className="text-sm text-muted-foreground">Lade …</p>;

  // Eine Akte, viele Eingaenge: wer aus der Firmenliste, vom Heute-Bildschirm
  // oder aus der Suche kam, will auch dorthin zurueck. Jede interne Navigation
  // hierher gibt ihre Herkunft als Router-State mit; nur ein kalter Link
  // (Reload, eingefuegte URL) hat keine und landet bei den Firmen — Kunden sind
  // Firmen, eine eigene Kundenliste gibt es nicht mehr.
  const herkunftPfad = (location.state as { from?: string } | null)?.from;
  const zurueckZu = herkunftPfad ?? '/firmen';
  // Die Herkunft ist nicht zwangslaeufig eine Liste (globale Suche) — dann
  // fuehrt der Link weiterhin richtig, sagt aber nur „Zurueck“.
  const zurueckLabel = listLabel(zurueckZu) ?? 'Zurück';

  if (notFound || !org) {
    return (
      <Card className="space-y-3 p-8 text-center">
        <p className="text-sm text-muted-foreground">
          Diese Firma gibt es nicht (mehr).
        </p>
        <Button variant="outline" onClick={() => navigate(zurueckZu)}>
          Zurück zur Übersicht
        </Button>
      </Card>
    );
  }

  const anschrift = [org.street, [org.postalCode, org.city].filter(Boolean).join(' ')]
    .filter(Boolean)
    .join(', ');

  const audienceKey = audiences.find((a) => a.id === org.audienceId)?.key;
  const audienceName = audiences.find((a) => a.id === org.audienceId)?.name;
  // Projekte gibt es nur, wo Projektarbeit vorkommt. Hängen an einem Kunden
  // trotzdem welche (die Zielgruppe wurde nachträglich umgestellt), bleibt der
  // Reiter: eine Akte darf nichts verstecken, was da ist.
  const projekteMoeglich =
    !audienceKey || !ZIELGRUPPEN_OHNE_PROJEKT.includes(audienceKey) || projekte.length > 0;
  const sichtbareReiter = REITER.filter(
    (r) => !r.nurKunde || (kunde && projekteMoeglich),
  );
  const angezeigterReiter = sichtbareReiter.some((r) => r.key === tab)
    ? tab
    : sichtbareReiter[0]?.key;

  return (
    <div className="space-y-4">
      <Link
        to={zurueckZu}
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" />
        {zurueckLabel}
      </Link>

      <Card className="p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="space-y-1">
            <h1 className="flex items-center gap-2 text-xl font-semibold">
              <Building2 className="h-5 w-5 text-muted-foreground" />
              {org.name}
            </h1>
            <p className="text-sm text-muted-foreground">
              {org.contactCount} {org.contactCount === 1 ? 'Ansprechpartner' : 'Ansprechpartner'}
              {org.openDealCount > 0 && ` · ${org.openDealCount} offene Deals`}
              {org.openOrderCount > 0 && ` · ${org.openOrderCount} offene Aufträge`}
            </p>

            <div className="flex flex-wrap items-center gap-2 text-sm">
              {audienceName && <Badge variant="neutral">{audienceName}</Badge>}
              <Badge variant={STATUS_VARIANT[org.status]}>
                {ORGANIZATION_STATUS_LABELS[org.status]}
              </Badge>
              {kunde ? (
                <>
                  <Select
                    value={kunde.status}
                    onChange={(e) => void statusSetzen(e.target.value as CustomerStatus)}
                    aria-label="Kundenstatus"
                    className="h-7 w-auto text-xs"
                  >
                    {CUSTOMER_STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {CUSTOMER_STATUS_LABELS[s]}
                      </option>
                    ))}
                  </Select>
                  <span className="text-muted-foreground">
                    Kunde seit{' '}
                    {kunde.since ? new Date(kunde.since).toLocaleDateString('de-DE') : '—'}
                    {kunde.primaryContactName && ` · ${kunde.primaryContactName}`}
                  </span>
                  <button
                    type="button"
                    className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                    title="Kundenstatus entfernen"
                    aria-label="Kundenstatus entfernen"
                    onClick={() => setZurueckstufen(true)}
                  >
                    <UserX className="h-4 w-4" />
                  </button>
                </>
              ) : (
                <Button variant="outline" size="sm" onClick={() => setBefoerdern(true)}>
                  <Handshake className="h-4 w-4" />
                  Zum Kunden befördern
                </Button>
              )}
            </div>
          </div>

          <div className="flex gap-2">
            {kontakte.some((c) => c.email && !c.optedOut) && (
              <Button variant="outline" onClick={() => setComposing(true)}>
                <Mail className="mr-2 h-4 w-4" />
                E-Mail
              </Button>
            )}
            <Button variant="outline" onClick={() => setEditing(true)}>
              <Pencil className="mr-2 h-4 w-4" />
              Bearbeiten
            </Button>
            <Button
              variant="ghost"
              size="icon"
              aria-label="Firma löschen"
              onClick={() => setDeleting(true)}
            >
              <Trash2 className="h-4 w-4 text-destructive" />
            </Button>
          </div>
        </div>

        {weg.length > 0 && (
          <div className="mt-4">
            <Wegleiste stationen={weg} />
          </div>
        )}

        <dl className="mt-6 grid gap-4 sm:grid-cols-3">
          <Detail label="Domain" icon={Globe}>
            {org.domain ?? '—'}
          </Detail>
          <Detail label="E-Mail" icon={Mail}>
            {org.email ? (
              <a className="hover:underline" href={`mailto:${org.email}`}>
                {org.email}
              </a>
            ) : (
              '—'
            )}
          </Detail>
          <Detail label="Telefon" icon={Phone}>
            {org.phone ? (
              <a className="hover:underline" href={`tel:${org.phone}`}>
                {org.phone}
              </a>
            ) : (
              '—'
            )}
          </Detail>
          <Detail label="Anschrift" icon={MapPin}>
            {anschrift || '—'}
          </Detail>
          <Detail label="Website" icon={Link2}>
            {org.website ? (
              <a
                className="hover:underline"
                href={org.website}
                target="_blank"
                rel="noreferrer noopener"
              >
                {org.website.replace(/^https?:\/\//, '')}
              </a>
            ) : (
              '—'
            )}
          </Detail>
          <Detail label="Branche" icon={Briefcase}>
            {org.industry ?? '—'}
          </Detail>
          <Detail label="Einsatzgebiet" icon={Map}>
            {org.region ?? '—'}
          </Detail>
        </dl>

        {org.notes && (
          <div className="mt-4">
            <h3 className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              <StickyNote className="h-3.5 w-3.5" />
              Notiz
            </h3>
            <p className="mt-1.5 whitespace-pre-wrap text-sm">{org.notes}</p>
          </div>
        )}

        {org.domain && (
          <div className="mt-4 rounded-md bg-muted/50 p-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm text-muted-foreground">
                Kontakte und noch nicht zugeordnete Nachrichten von{' '}
                <strong>{org.domain}</strong> dieser Firma zuschlagen.
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={async () => {
                  const res = await crm.organizations.adoptByDomain(org.id);
                  setHinweis(
                    res.contacts || res.messages
                      ? `${res.contacts} Kontakte und ${res.messages} Nachrichten zugeordnet.`
                      : 'Nichts gefunden, was noch zuzuordnen wäre.',
                  );
                  await load();
                }}
              >
                <Link2 className="mr-2 h-4 w-4" />
                Zuordnen
              </Button>
            </div>
            {hinweis && <p className="mt-2 text-sm">{hinweis}</p>}
          </div>
        )}
      </Card>

      <div className="flex gap-1 rounded-lg border border-border bg-card p-1">
        {sichtbareReiter.map((r) => (
          <button
            key={r.key}
            type="button"
            onClick={() => setTab(r.key)}
            className={cn(
              'rounded-md px-3 py-1.5 text-sm transition-colors',
              angezeigterReiter === r.key
                ? 'bg-primary text-primary-foreground'
                : 'hover:bg-muted',
            )}
          >
            {r.label}
          </button>
        ))}
      </div>

      {angezeigterReiter === 'overview' && (
        <UeberblickTab
          projekte={projekteMoeglich ? projekte : null}
          istKunde={Boolean(kunde)}
          herkunft={herkunft}
          mails={mails}
          kontakte={kontakte}
          hauptansprechpartner={kunde?.primaryContactName ?? null}
          onOeffnen={setTab}
        />
      )}

      {angezeigterReiter === 'projects' && kunde && projekteMoeglich && (
        <ProjectsTab customerId={kunde.id} organizationId={org.id} />
      )}

      {angezeigterReiter === 'contacts' && (
        <ContactsTab
          organizationId={org.id}
          organizationName={org.name}
          defaultAudienceId={org.audienceId}
          contacts={kontakte}
          audiences={audiences}
          onChanged={load}
        />
      )}

      {angezeigterReiter === 'campaigns' && <CampaignsTab contacts={kontakte} />}

      {angezeigterReiter === 'mail' && <MailTab mails={mails} onChanged={load} />}

      {angezeigterReiter === 'orders' && <OrdersTab orders={auftraege} onChanged={() => void load()} />}

      {angezeigterReiter === 'timeline' && <TimelineTab activities={verlauf} />}

      {editing && (
        <OrganizationDialog
          organization={org}
          audiences={audiences}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            void load();
          }}
        />
      )}

      {deleting && (
        <DeleteOrganizationDialog
          organization={org}
          onClose={() => setDeleting(false)}
          onDeleted={() => navigate('/firmen')}
        />
      )}

      {composing && (
        <ComposeDialog
          contacts={kontakte.filter((c) => c.email && !c.optedOut)}
          onClose={() => setComposing(false)}
          onSent={() => void load()}
        />
      )}

      {zurueckstufen && kunde && (
        <DemoteCustomerDialog
          customer={kunde}
          onClose={() => setZurueckstufen(false)}
          onDone={() => {
            setZurueckstufen(false);
            void load();
          }}
        />
      )}

      {befoerdern && (
        <PromoteCustomerDialog
          organizationId={org.id}
          onClose={() => setBefoerdern(false)}
          onDone={() => {
            setBefoerdern(false);
            void load();
          }}
        />
      )}
    </div>
  );
}

function Detail({
  label,
  icon: Icon,
  children,
}: {
  label: string;
  icon: typeof Globe;
  children: React.ReactNode;
}) {
  return (
    <div>
      <dt className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        <Icon className="h-3.5 w-3.5" />
        {label}
      </dt>
      <dd className="mt-1 text-sm">{children}</dd>
    </div>
  );
}
